/* An agent's ink buddy: a soft creature made of drops of ink, mirrored down the middle like a
   Rorschach card. Mirror symmetry is the body plan of every animal and face, so the eye reads
   it as alive; the kinds are the round, friendly ones people collect as plushies (a ghost, a
   bear, a bunny, a slime, ...). The family picks a group of kinds; the agent's saved seed
   (`glyphKey`) picks the kind and randomises everything about it, so every recipe is its own
   buddy and an agent always looks the same.

   The body is plain circles; the renderer fuses them with a "gooey" filter (blur, then a sharp
   alpha threshold), so neighbours pool into one body the way ink does. The face sits on top,
   unfiltered. A working agent loops through the frames: it hops, floats or bobs as its kind
   does, ears and tentacles wiggle, pupils look around, eyes blink. Coordinates are a 100-unit
   box centred on 0. */
import { glyphKey, type GlyphFamily } from "./glyph";

export interface Part {
  /** Path frames, evenly spaced over one loop; first = at rest. */
  frames: string[];
  /** ink: fused by the gooey filter. hole: the background colour over the ink (eyes, mouth,
      spots, sparkles). dot: plain ink on top (pupils). blush: a faint background-colour wash. */
  paint: "ink" | "hole" | "dot" | "blush";
}
export interface Buddy { kind: string; parts: Part[]; /** Seconds for one loop of the frames. */ seconds: number }

const FRAMES = 16;
const TAU = 2 * Math.PI;
const n2 = (v: number) => (Math.abs(v) < 0.005 ? "0" : v.toFixed(2));
const ellipse = (x: number, y: number, rx: number, ry: number) =>
  `M${n2(x - rx)} ${n2(y)} A${n2(rx)} ${n2(ry)} 0 1 0 ${n2(x + rx)} ${n2(y)} A${n2(rx)} ${n2(ry)} 0 1 0 ${n2(x - rx)} ${n2(y)}`;
/** A crescent under (x, y): a smile. `up` turns it over: a happy closed eye (^). */
const crescent = (x: number, y: number, w: number, up = false) => {
  const [a, b] = up ? [1, 0] : [0, 1];
  return `M${n2(x - w)} ${n2(y)} A${n2(w)} ${n2(w * 0.8)} 0 0 ${a} ${n2(x + w)} ${n2(y)} A${n2(w)} ${n2(w * 0.38)} 0 0 ${b} ${n2(x - w)} ${n2(y)} Z`;
};
const phases = Array.from({ length: FRAMES }, (_, k) => TAU * k / FRAMES);

/** A repeatable random stream from the recipe: the same seed always draws the same buddy. */
function stream(key: string) {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  let s = h >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A drop of ink; x > 0 is drawn on both sides, x = 0 on the fold. It drifts by (dx, dy) and
    swells by `swell` round its `phase`. */
interface Drop { x: number; y: number; r: number; dx: number; dy: number; swell: number; phase: number }
interface Face {
  /** The drop the face rides on; eyes sit `gap` host radii either side, `y` below its centre. */
  host: Drop;
  /** Eyes centred on this mirrored pair of drops instead (a frog's eyes on top of its head). */
  eyesOn?: Drop;
  gap: number; y: number; eye: number;
  mouth: "smile" | "cat" | "o" | "none";
}
interface Plan { drops: Drop[]; face: Face; motion: "hop" | "float" | "bob"; spots?: [number, number, number][] }

type Rand = (lo: number, hi: number) => number;
type Make = (x: number, y: number, r: number, motion?: Partial<Drop>) => Drop;
type Kind = (b: Rand, m: Make, pick: <T>(...options: T[]) => T) => Plan;

/** A wiggle for ears, arms and tentacles: they move more than the body. */
const wiggle = (b: Rand): Partial<Drop> => ({ dx: b(0.8, 1.8), dy: b(1, 2.2) });

const KINDS: Record<string, Kind> = {
  ghost: (b, m, pick) => {
    const dome = m(0, b(-8, -4), b(15, 18));
    const hem = dome.y + dome.r * b(0.75, 0.9);
    return {
      motion: "float",
      drops: [dome, m(0, hem, b(5, 6.5)), m(b(6, 8), hem + b(-1, 2), b(4.5, 6)), m(b(12, 15), hem - b(1, 4), b(4, 5.5)),
        m(dome.r * b(1, 1.12), dome.y + b(3, 7), b(3, 4.2), wiggle(b))],
      face: { host: dome, gap: b(0.3, 0.42), y: b(0.05, 0.25), eye: b(0.19, 0.25), mouth: pick("o", "smile", "none") },
    };
  },
  jellyfish: (b, m, pick) => {
    const bell = m(0, b(-12, -8), b(14, 16));
    const tentacle = (x: number) => [0, 1, 2].map((i) => m(x + b(-1, 1), bell.y + bell.r * 0.8 + 6 + i * 6, 3.4 - i * 0.8, { dx: 1 + i * 0.6, dy: 0.6, phase: i * 0.7 }));
    return {
      motion: "float",
      drops: [bell, m(b(9, 11), bell.y + 5, b(7, 8.5)), ...tentacle(b(4, 6)), ...tentacle(b(11, 13))],
      face: { host: bell, gap: b(0.3, 0.4), y: b(0.15, 0.3), eye: b(0.18, 0.22), mouth: pick("smile", "o") },
    };
  },
  cloud: (b, m, pick) => {
    const top = m(0, b(-8, -5), b(12, 14));
    return {
      motion: "float",
      drops: [top, m(b(10, 12), b(-2, 1), b(9, 11)), m(b(17, 20), b(5, 8), b(6, 7.5)), m(0, b(6, 8), b(10, 11)), m(b(9, 11), b(7, 9), b(8, 9.5))],
      face: { host: top, gap: b(0.42, 0.55), y: b(0.45, 0.65), eye: b(0.18, 0.22), mouth: pick("smile", "smile", "o") },
    };
  },
  bear: (b, m, pick) => {
    const head = m(0, b(1, 5), b(17, 19));
    return {
      motion: "bob",
      drops: [head, m(head.r * b(0.66, 0.8), head.y - head.r * b(0.75, 0.88), b(6, 7.5), wiggle(b)), m(head.r * b(0.5, 0.6), head.y + head.r * b(0.42, 0.55), b(6.5, 8))],
      face: { host: head, gap: b(0.34, 0.42), y: b(0.02, 0.18), eye: b(0.17, 0.22), mouth: pick("smile", "cat", "none") },
    };
  },
  hamster: (b, m, pick) => {
    const head = m(0, b(0, 4), b(14, 16));
    return {
      motion: "bob",
      drops: [head, m(b(10, 12), head.y + b(5, 8), b(10, 12)), m(b(10, 12), head.y - b(10, 13), b(4, 5), wiggle(b)), m(b(5, 7), head.y + b(17, 20), b(2.6, 3.4))],
      face: { host: head, gap: b(0.45, 0.6), y: b(0.05, 0.2), eye: b(0.15, 0.19), mouth: pick("cat", "o") },
    };
  },
  owl: (b, m, pick) => {
    const body = m(0, b(0, 4), b(17, 19));
    return {
      motion: "bob",
      drops: [body, m(b(11, 13), body.y - body.r * 0.86, b(3.4, 4.2), wiggle(b)),
        m(body.r * b(0.85, 0.95), body.y + b(3, 7), b(5.5, 7), wiggle(b)), m(b(4, 6), body.y + body.r * 0.98, b(2.2, 3))],
      face: { host: body, gap: b(0.4, 0.46), y: b(-0.22, -0.08), eye: b(0.27, 0.31), mouth: pick("none", "o") },
    };
  },
  bunny: (b, m, pick) => {
    const head = m(0, b(6, 10), b(15, 17));
    const ear = b(5.5, 7.5);
    return {
      motion: "hop",
      drops: [head, m(ear, head.y - head.r * 0.9, b(4.6, 5.4), wiggle(b)), m(ear + 0.8, head.y - head.r * 1.4, b(4.2, 5), wiggle(b)), m(ear + 1.5, head.y - head.r * 1.85, b(3.5, 4.3), wiggle(b)),
        m(b(6, 8), head.y + head.r * 0.95, b(3.2, 4))],
      face: { host: head, gap: b(0.34, 0.42), y: b(0.05, 0.2), eye: b(0.17, 0.22), mouth: pick("cat", "smile", "none") },
    };
  },
  "lop bunny": (b, m, pick) => {
    const head = m(0, b(-2, 2), b(16, 18));
    return {
      motion: "hop",
      drops: [head, m(head.r * 0.95, head.y - b(1, 3), b(5.2, 6), wiggle(b)), m(head.r * 1.12, head.y + b(5, 7), b(4.8, 5.6), wiggle(b)), m(head.r * 1.18, head.y + b(12, 14), b(4, 4.8), wiggle(b)),
        m(b(6, 8), head.y + head.r * 1.02, b(3.4, 4.2))],
      face: { host: head, gap: b(0.34, 0.42), y: b(0.05, 0.2), eye: b(0.17, 0.22), mouth: pick("cat", "smile") },
    };
  },
  cat: (b, m, pick) => {
    const head = m(0, b(3, 6), b(16, 18));
    const ear = b(9, 11);
    return {
      motion: "bob",
      drops: [head, m(ear, head.y - head.r * 0.72, b(5.5, 6.5), wiggle(b)), m(ear + 2.5, head.y - head.r * 1.02, b(3.5, 4.2), wiggle(b)), m(ear + 4.5, head.y - head.r * 1.25, b(1.8, 2.4), wiggle(b)),
        m(head.r * b(0.55, 0.65), head.y + head.r * b(0.38, 0.5), b(6, 7.5))],
      face: { host: head, gap: b(0.36, 0.44), y: b(0.02, 0.16), eye: b(0.17, 0.22), mouth: pick("cat", "cat", "none") },
    };
  },
  chick: (b, m, pick) => {
    const body = m(0, b(2, 6), b(17, 19));
    return {
      motion: "bob",
      drops: [body, m(0, body.y - body.r - b(3.5, 4.5), b(2, 2.5), wiggle(b)), m(b(2.6, 3.2), body.y - body.r - b(2.2, 3), b(1.6, 2)),
        m(body.r * b(0.92, 1.02), body.y + b(2, 6), b(4.5, 5.8), { dx: 1.5, dy: 2.5 }), m(b(5, 7), body.y + body.r * 0.98, b(2.2, 2.8))],
      face: { host: body, gap: b(0.32, 0.4), y: b(-0.1, 0.08), eye: b(0.16, 0.2), mouth: pick("o", "smile") },
    };
  },
  slime: (b, m, pick) => {
    const base = m(0, b(9, 12), b(13, 15));
    return {
      motion: "hop",
      drops: [base, m(b(9, 11), base.y + b(1, 3), b(8.5, 10)), m(0, base.y - b(9, 11), b(10, 11.5)), m(0, base.y - b(17, 19), b(6.5, 7.5)), m(0, base.y - b(23, 25), b(3.5, 4.2), { dy: 1.5 })],
      face: { host: base, gap: b(0.36, 0.46), y: b(-0.3, -0.15), eye: b(0.18, 0.23), mouth: pick("smile", "smile", "o") },
    };
  },
  mochi: (b, m, pick) => {
    const middle = m(0, b(3, 6), b(13, 14.5));
    return {
      motion: "hop",
      drops: [middle, m(b(9, 11), middle.y + b(1, 3), b(10.5, 12)), m(b(17, 19), middle.y + b(5, 7), b(5.5, 7)), m(b(5, 7), middle.y + b(11, 13), b(3, 3.8))],
      face: { host: middle, gap: b(0.5, 0.65), y: b(-0.05, 0.15), eye: b(0.17, 0.21), mouth: pick("smile", "cat", "o") },
    };
  },
  frog: (b, m, pick) => {
    const head = m(0, b(5, 8), b(14, 15.5));
    const bump = m(b(9, 11), head.y - b(9, 11), b(6.5, 7.5));
    return {
      motion: "hop",
      drops: [head, m(b(9, 11), head.y + b(2, 4), b(10.5, 12)), bump, m(b(14, 17), head.y + b(13, 15), b(3.4, 4.2), wiggle(b))],
      face: { host: head, eyesOn: bump, gap: 0, y: 0, eye: b(0.48, 0.58), mouth: pick("smile", "smile", "cat") },
    };
  },
  mushroom: (b, m, pick) => {
    const cap = m(0, b(-11, -8), b(15, 17));
    const stem = m(0, cap.y + b(18, 21), b(9, 10.5));
    return {
      motion: "bob",
      drops: [cap, m(b(11, 13), cap.y + b(2, 4), b(10, 11.5)), m(b(18, 20), cap.y + b(5, 7), b(5.5, 6.5)), stem, m(0, stem.y + 6, b(8, 9))],
      face: { host: stem, gap: b(0.36, 0.44), y: b(-0.15, 0.05), eye: b(0.2, 0.25), mouth: pick("smile", "o") },
      spots: [[0, cap.y - b(6, 9), b(2.4, 3.2)], [b(9, 12), cap.y - b(1, 3), b(2.2, 3)], [b(16, 19), cap.y + b(3, 5), b(1.4, 2)]],
    };
  },
};

const FAMILIES: Record<GlyphFamily, string[]> = {
  lens: ["ghost", "jellyfish", "cloud"],
  code: ["bear", "hamster", "owl"],
  signal: ["bunny", "lop bunny"],
  flow: ["cat", "chick"],
  dialog: ["slime", "mochi"],
  spark: ["frog", "mushroom"],
};

export function drawBuddy(seed: string): Buddy {
  const key = glyphKey(seed);
  const random = stream(key);
  const b: Rand = (lo, hi) => lo + (hi - lo) * random();
  const pick = <T,>(...options: T[]) => options[Math.floor(random() * options.length)];
  // A drop on the fold never moves sideways, or the mirror would break.
  const make: Make = (x, y, r, motion = {}) => ({
    x, y, r, dx: b(0.3, 0.9), dy: b(0.3, 0.9), swell: b(0.02, 0.05), phase: b(0, TAU), ...motion, ...(x === 0 ? { dx: 0 } : {}),
  });
  const kind = pick(...FAMILIES[key.split(":")[2] as GlyphFamily]);
  const { drops, face, motion, spots = [] } = KINDS[kind](b, make, pick);
  const eyes = random() < 0.2 ? "happy" : "round";
  const blush = random() < 0.55;
  const mouthSize = b(0.12, 0.18);

  /* The whole body's motion: a hop squashes as it lands, a float rises and sinks, a bob nods. */
  const body = (p: number) => {
    if (motion === "hop") {
      const h = Math.abs(Math.sin(p)), squash = Math.max(0, 1 - h * 4);
      return { lift: -6 * h, sx: 1 + 0.1 * squash, sy: 1 - 0.1 * squash };
    }
    if (motion === "float") return { lift: -3.5 * Math.sin(p), sx: 1 + 0.015 * Math.sin(2 * p), sy: 1 - 0.015 * Math.sin(2 * p) };
    return { lift: -1.6 * Math.sin(2 * p), sx: 1 + 0.025 * Math.sin(2 * p), sy: 1 - 0.025 * Math.sin(2 * p) };
  };
  const drift = (d: Drop, p: number, side: 1 | -1) => ({
    x: side * (d.x + d.dx * Math.sin(p + d.phase)), y: d.y + d.dy * Math.sin(2 * p + d.phase), r: d.r * (1 + d.swell * Math.sin(p + d.phase)),
  });
  const circles = (p: number) => {
    const t = body(p);
    return drops.flatMap((d) => (d.x === 0 ? [drift(d, p, 1)] : [drift(d, p, 1), drift(d, p, -1)]))
      .map((c) => ({ x: c.x * t.sx, y: c.y * t.sy + t.lift, rx: c.r * t.sx, ry: c.r * t.sy }));
  };
  // Fit every frame inside a 41-unit half-width, the blur's spread included.
  const reach = Math.max(...phases.flatMap((p) => circles(p).map((c) => Math.max(Math.abs(c.x) + c.rx, Math.abs(c.y) + c.ry))));
  const s = Math.min(1.3, 41 / reach);
  const frames = (draw: (p: number, k: number) => string) => phases.map(draw);
  const parts: Part[] = [{ paint: "ink", frames: frames((p) => circles(p).map((c) => ellipse(c.x * s, c.y * s, c.rx * s, c.ry * s)).join(" ")) }];

  // Where the eyes are in frame k: centre of the right eye, its radius, how open, where it looks.
  const look = b(0, TAU);
  const eye = (p: number, k: number) => {
    const t = body(p), host = drift(face.host, p, 1), on = face.eyesOn && drift(face.eyesOn, p, 1);
    const r = on ? on.r * face.eye : host.r * face.eye * 1.2;
    const x = on ? on.x : host.r * face.gap, y = on ? on.y : host.y + host.r * face.y;
    const open = k === FRAMES / 2 ? 0.08 : Math.abs(k - FRAMES / 2) === 1 ? 0.55 : 1;
    return { x: x * t.sx * s, y: (y * t.sy + t.lift) * s, r: r * s, sx: t.sx, sy: t.sy, open, gaze: [0.28 * Math.cos(p + look), 0.22 * Math.sin(p + look) + 0.1], t };
  };
  const both = (x: number, y: number, draw: (x: number, y: number) => string) => `${draw(x, y)} ${draw(-x, y)}`;

  if (blush) {
    parts.push({ paint: "blush", frames: frames((p, k) => {
      const e = eye(p, k);
      return both(e.x + e.r * 0.75, e.y + e.r * 1.25, (x, y) => ellipse(x, y, e.r * 0.8, e.r * 0.42));
    }) });
  }
  if (eyes === "happy") {
    parts.push({ paint: "hole", frames: frames((p, k) => { const e = eye(p, k); return both(e.x, e.y + e.r * 0.3, (x, y) => crescent(x, y, e.r * 1.05, true)); }) });
  } else {
    parts.push({ paint: "hole", frames: frames((p, k) => { const e = eye(p, k); return both(e.x, e.y, (x, y) => ellipse(x, y, e.r * e.sx, e.r * e.sy * e.open)); }) });
    // Pupils and their sparkle look the same way in both eyes, lit from the same side.
    const pupil = (p: number, k: number) => { const e = eye(p, k); return { e, pr: e.r * 0.58, gx: e.gaze[0] * e.r, gy: e.gaze[1] * e.r, shut: Math.min(1, e.open * 1.6) }; };
    parts.push({ paint: "dot", frames: frames((p, k) => {
      const { e, pr, gx, gy, shut } = pupil(p, k);
      return `${ellipse(e.x + gx, e.y + gy, pr, pr * shut)} ${ellipse(-e.x + gx, e.y + gy, pr, pr * shut)}`;
    }) });
    parts.push({ paint: "hole", frames: frames((p, k) => {
      const { e, pr, gx, gy, shut } = pupil(p, k), r = pr * 0.34 * shut;
      return `${ellipse(e.x + gx + pr * 0.35, e.y + gy - pr * 0.38, r, r)} ${ellipse(-e.x + gx + pr * 0.35, e.y + gy - pr * 0.38, r, r)}`;
    }) });
  }
  if (face.mouth !== "none") {
    parts.push({ paint: "hole", frames: frames((p, k) => {
      const e = eye(p, k), w = face.host.r * mouthSize * s;
      const y = face.eyesOn ? (face.host.y * e.t.sy + e.t.lift) * s + w * 0.6 : e.y + e.r * 1.25;
      if (face.mouth === "cat") return `${crescent(-w * 0.5, y, w * 0.55)} ${crescent(w * 0.5, y, w * 0.55)}`;
      if (face.mouth === "o") return ellipse(0, y + w * 0.35, w * 0.42, w * 0.5);
      return crescent(0, y, w);
    }) });
  }
  if (spots.length) {
    parts.push({ paint: "hole", frames: frames((p) => {
      const t = body(p);
      return spots.map(([x, y, r]) => {
        const at = (sx: number) => ellipse(sx * t.sx * s, (y * t.sy + t.lift) * s, r * t.sx * s, r * t.sy * s);
        return x === 0 ? at(0) : `${at(x)} ${at(-x)}`;
      }).join(" ");
    }) });
  }
  return { kind, parts, seconds: 3 };
}
